import React, {useEffect, useMemo, useRef, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {Button} from "primereact/button";
import {Badge} from "primereact/badge";
import {ConfirmDialog, confirmDialog} from "primereact/confirmdialog";
import {Dropdown} from "primereact/dropdown";
import {IconField} from "primereact/iconfield";
import {InputIcon} from "primereact/inputicon";
import {InputText} from "primereact/inputtext";
import {InputTextarea} from "primereact/inputtextarea";
import {Message} from "primereact/message";
import {OverlayPanel} from "primereact/overlaypanel";
import {saveUser} from "../../../auth/auth-slice";
import api, {normalizeApiError} from "../../../system/api";
import Avatar, {presetAvatarUrl} from "../../../system/components/avatar";
import Icon from "../../../system/components/icon";
import InputLabel from "../../../system/components/input-label";
import {useTranslate} from "../../../system/useTranslate";
import PageHeader from "../../components/page-header";
import UploadDialog from "../../components/upload-dialog";
import AccountSecurity from "./account-security";

const PRESET_AVATARS = Array.from({length: 24}, (_, index) => (
    `preset-avatar-${String(index + 1).padStart(2, "0")}.png`
));

const profileForm = (user) => ({
    fullname: user?.fullname || "",
    jobPosition: user?.jobPosition || "",
    about: user?.about || "",
    email: user?.email || "",
    timeZone: user?.timeZone || "Asia/Tehran",
});

function ProfileSetting({onCancel}) {
    const {user} = useSelector((state) => state.auth);
    const translate = useTranslate();
    const dispatch = useDispatch();
    const [form, setForm] = useState(() => profileForm(user));
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState(null);

    useEffect(() => setForm(profileForm(user)), [user]);

    const timeZones = useMemo(() => [
        {label: translate("profile.timezone.tehran"), value: "Asia/Tehran"},
        {label: translate("profile.timezone.utc"), value: "UTC"},
        {label: translate("profile.timezone.dubai"), value: "Asia/Dubai"},
        {label: translate("profile.timezone.berlin"), value: "Europe/Berlin"},
    ], [translate]);

    const updateForm = (field, value) => {
        setMessage(null);
        setForm((current) => ({...current, [field]: value}));
    };

    const submit = async () => {
        setSaving(true);
        setMessage(null);
        try {
            const response = await api.put("/updateUser", form);
            const updatedUser = response?.data || {};
            dispatch(saveUser({...user, ...updatedUser}));
            setMessage({severity: "success", text: translate("profile.save-success")});
        } catch (error) {
            const key = normalizeApiError(error).message;
            const translated = translate(key);
            setMessage({
                severity: "error",
                text: translated === key ? translate("profile.save-error") : translated,
            });
        } finally {
            setSaving(false);
        }
    };

    const updateAuthenticatedUser = (patch) => dispatch(saveUser({...user, ...patch}));

    return (
        <div className="h-full overflow-auto surface-ground">
            <div className="mx-auto p-3 md:p-5 flex flex-column gap-4 max-w-screen-lg">
                <PageHeader title="profile-info" description="profile-info-description"/>
                <ProfilePhoto user={user} onChange={updateAuthenticatedUser}/>

                <section className="surface-card border-1 border-100 border-round-xl p-3 md:p-4">
                    <div className="grid formgrid">
                        <InputLabel className="col-12 md:col-6 mb-3" label={translate("fullname")}>
                            <InputText className="w-full text-xs" value={form.fullname}
                                onChange={(event) => updateForm("fullname", event.target.value)}/>
                        </InputLabel>
                        <InputLabel className="col-12 md:col-6 mb-3" label={translate("job-title")}>
                            <InputText className="w-full text-xs" value={form.jobPosition}
                                onChange={(event) => updateForm("jobPosition", event.target.value)}/>
                        </InputLabel>
                        <InputLabel className="col-12 mb-3" label={translate("bio-summary")}>
                            <InputTextarea className="w-full text-xs" rows={4} autoResize value={form.about}
                                onChange={(event) => updateForm("about", event.target.value)}/>
                        </InputLabel>
                        <InputLabel className="col-12 md:col-6 mb-3" label={translate("phone-number")}>
                            <IconField dir="ltr" iconPosition="left" className="w-full">
                                <InputIcon className="pi pi-mobile"/>
                                <InputText className="w-full text-xs" value={user?.phonenumber || ""} readOnly disabled/>
                            </IconField>
                        </InputLabel>
                        <InputLabel className="col-12 md:col-6 mb-3" label={translate("email")}>
                            <IconField dir="ltr" iconPosition="left" className="w-full">
                                <InputIcon className="pi pi-envelope"/>
                                <InputText className="w-full text-xs" value={form.email}
                                    onChange={(event) => updateForm("email", event.target.value)}/>
                            </IconField>
                        </InputLabel>
                        <InputLabel className="col-12 mb-2" label={translate("time-zone")}>
                            <Dropdown className="w-full text-xs" value={form.timeZone} options={timeZones}
                                onChange={(event) => updateForm("timeZone", event.value)}
                                pt={{input: {className: "text-xs"}, item: {className: "text-xs text-right"}, panel: {className: "rtl"}}}/>
                        </InputLabel>
                    </div>
                    {message && <Message severity={message.severity} text={message.text} className="w-full text-xs mt-2"/>}
                    <div className="flex flex-wrap gap-2 justify-content-end mt-4">
                        <Button type="button" outlined className="p-button-sm text-xs"
                            label={translate("cancel")} onClick={() => onCancel?.()}/>
                        <Button type="button" className="p-button-sm text-xs" loading={saving}
                            label={translate("save-change")} onClick={submit}/>
                    </div>
                </section>

                <AccountSecurity/>
            </div>
        </div>
    );
}

function ProfilePhoto({user, onChange}) {
    const translate = useTranslate();
    const avatarPanelRef = useRef(null);
    const [uploadDialogVisible, setUploadDialogVisible] = useState(false);
    const [pendingAvatar, setPendingAvatar] = useState("");
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [error, setError] = useState("");

    const requestErrorMessage = (requestError, fallbackKey) => {
        const key = normalizeApiError(requestError).message;
        const translated = translate(key);
        return translated === key ? translate(fallbackKey) : translated;
    };

    const selectPresetAvatar = async (presetAvatar) => {
        if (pendingAvatar || presetAvatar === user?.presetAvatar) return;
        setPendingAvatar(presetAvatar);
        setError("");
        try {
            if (user?.profileImage) {
                await api.delete("/users/me/profile-image", {suppressGlobalErrorHandler: true});
            }
            const response = await api.put("/updateUser", {presetAvatar});
            onChange?.({...response?.data, profileImage: null, presetAvatar});
            avatarPanelRef.current?.hide();
        } catch (requestError) {
            setError(requestErrorMessage(requestError, "profile.save-error"));
        } finally {
            setPendingAvatar("");
        }
    };

    const deleteProfilePhoto = async () => {
        setDeleteLoading(true);
        setError("");
        try {
            if (user?.profileImage) {
                await api.delete("/users/me/profile-image", {suppressGlobalErrorHandler: true});
            }
            if (user?.presetAvatar) await api.put("/updateUser", {presetAvatar: null});
            onChange?.({profileImage: null, presetAvatar: null});
        } catch (requestError) {
            setError(requestErrorMessage(requestError, "profile-image.error.delete-failed"));
        } finally {
            setDeleteLoading(false);
        }
    };

    const confirmDelete = () => {
        if ((!user?.profileImage && !user?.presetAvatar) || deleteLoading) return;
        confirmDialog({
            message: translate("profile-image.delete-confirm-message"),
            header: translate("profile-image.delete-confirm-title"),
            icon: "pi pi-exclamation-triangle",
            acceptLabel: translate("confirm"),
            rejectLabel: translate("cancel"),
            acceptClassName: "p-button-danger text-xs",
            rejectClassName: "p-button-text text-xs",
            accept: deleteProfilePhoto,
        });
    };

    const openAvatarPanel = (event) => avatarPanelRef.current?.toggle(event);

    const openCustomUpload = () => {
        avatarPanelRef.current?.hide();
        setUploadDialogVisible(true);
    };

    return (
        <section className="surface-card border-1 border-100 border-round-xl p-3 md:p-4 flex flex-column gap-4">
            <div className="flex flex-column md:flex-row align-items-center md:align-items-start gap-4">
                <div className="relative w-6rem h-6rem flex-shrink-0">
                    <Avatar className="w-full h-full shadow-2 border-white border-3" size="xlarge" memberData={user}/>
                    <Button rounded type="button" icon={<Icon className="text-sm" icon="mi mi:edit"/>}
                        className="absolute bottom-0 left-0 w-2rem h-2rem p-0 gap-2 shadow-1"
                        aria-label={translate("profile.edit-photo")} onClick={openAvatarPanel}/>
                </div>
                <div className="flex-1 min-w-0 text-center md:text-right">
                    <PageHeader title="profile-photo" description="profile-photo-description"/>
                    <div className="flex flex-wrap justify-content-center md:justify-content-start gap-2 mt-3">
                        <Button className="p-button-sm text-xs" label={translate("change-Photo")}
                            onClick={openAvatarPanel}/>
                        <Button className="p-button-sm text-xs" outlined severity="danger"
                            label={translate("delete-photo")} loading={deleteLoading}
                            disabled={(!user?.profileImage && !user?.presetAvatar) || deleteLoading}
                            onClick={confirmDelete}/>
                    </div>
                </div>
            </div>

            <OverlayPanel ref={avatarPanelRef} className="rtl shabnam-font w-22rem max-w-full"
                pt={{content: {className: "p-3"}}}>
                <div className="flex flex-column gap-3">
                    <div>
                        <strong className="block text-xs text-900 mb-1">{translate("profile.preset-avatars-title")}</strong>
                        <small className="block text-xs text-500">{translate("profile.preset-avatars-description")}</small>
                    </div>
                    <div className="grid m-0 max-h-24rem overflow-y-auto pr-1">
                    {PRESET_AVATARS.map((presetAvatar, index) => {
                        const selected = presetAvatar === user?.presetAvatar;
                        const label = translate("profile.preset-avatar-label").replace("{{number}}", index + 1);
                        return (
                            <div className="col-4 p-2 flex justify-content-center" key={presetAvatar}>
                                <button type="button" title={label} aria-label={label}
                                    aria-pressed={selected} disabled={Boolean(pendingAvatar)}
                                    className={`relative w-5rem h-5rem border-circle p-1 cursor-pointer ${selected ? "border-3 border-primary surface-100 shadow-3" : "border-1 border-200 surface-card"}`}
                                    onClick={() => selectPresetAvatar(presetAvatar)}>
                                    <span className="block w-full h-full border-circle overflow-hidden">
                                        <img src={presetAvatarUrl(presetAvatar)} alt="" className="block w-full h-full border-circle object-fit-cover"/>
                                    </span>
                                    {selected && <Badge severity="success"
                                        value={<Icon icon="mi mi:check" className="text-sm"/>}
                                        aria-label={translate("profile.preset-avatar-selected")}
                                        className="absolute top-0 right-0 w-2rem h-2rem border-circle border-2 border-white shadow-2 z-2 flex align-items-center justify-content-center"/>}
                                    {pendingAvatar === presetAvatar && <i className="pi pi-spin pi-spinner absolute top-50 left-50 text-primary"/>}
                                </button>
                            </div>
                        );
                    })}
                    </div>
                    <div className="border-top-1 border-100 pt-3">
                        <Button type="button" outlined className="w-full p-button-sm text-xs gap-2"
                            icon={<Icon icon="mi mi:upload"/>}
                            label={translate("profile.upload-custom-photo")}
                            onClick={openCustomUpload}/>
                    </div>
                </div>
            </OverlayPanel>
            {error && <Message severity="error" text={error} className="w-full text-xs"/>}
            <ConfirmDialog className="rtl"/>
            <UploadDialog uploadMode="profile" visible={uploadDialogVisible} setVisible={setUploadDialogVisible}
                updateContentType={(items) => {
                    const [profileImage] = items;
                    if (profileImage?.id) onChange?.({profileImage, presetAvatar: null});
                }}/>
        </section>
    );
}

export default ProfileSetting;
